<template>
  <ds-rollover ref="rollover" title="Alocações e Retenções">
    <ds-page>
      <ds-form
        :submit-action="onSubmit"
        :error-message="errorMessage"
        @submit-success="onSubmitSuccess"
        @submit-error="onSubmitError">
        <ds-fieldset title="Dados do tomador de serviço e retenção" />
        <ds-row>
          <ds-col size="3">
            <ds-field label="Nome/Razão Social">
              <ds-search-select
                v-model="serviceTakerAggregated.serviceTaker"
                required
                compare-value-by="id"
                :selected-option="serviceTakerSelected"
                :on-fetch="onFetchServiceTaker"
                :on-fetch-success="onFetchServiceTakerSuccess"
                :on-query="onQueryServiceTaker"
                :on-query-success="onQueryServiceTakerSuccess"
                @input="onServiceTakerChange">
                <ds-option
                  v-for="serviceTaker in serviceTakers"
                  :key="serviceTaker.id"
                  :value="serviceTaker">
                  {{ serviceTaker.serviceTakerName }}
                </ds-option>
              </ds-search-select>
            </ds-field>
          </ds-col>
          <ds-col size="3">
            <ds-field label="Tipo de Inscrição">
              <ds-input
                v-model="serviceTakerAggregated.serviceTaker.registrationType.description"
                disabled
                required>
              </ds-input>
            </ds-field>
          </ds-col>
          <ds-col size="3">
            <ds-field label="CNPJ/CNO">
              <ds-input
                v-model="serviceTakerAggregated.serviceTaker.registration"
                disabled
                required>
              </ds-input>
            </ds-field>
          </ds-col>
          <ds-col size="3">
            <ds-field label="Competência">
              <ds-date-input
                v-model="serviceTakerAggregated.competence"
                :on-change="onCompetenceChange"
                type="month"
                required>
              </ds-date-input>
            </ds-field>
          </ds-col>
        </ds-row>

        <ds-row>
          <ds-col size="3">
            <ds-field label="Número da Nota">
              <ds-input
                v-model="serviceTakerAggregated.invoiceNumber"
                required
                maxlength="9" />
            </ds-field>
          </ds-col>

          <ds-col size="3">
            <ds-field label="Data de Emissão">
              <ds-date-input
                v-model="serviceTakerAggregated.issueDate"
                type="day"
                required>
              </ds-date-input>
            </ds-field>
          </ds-col>

          <ds-col size="3">
            <ds-field label="Valor Bruto">
              <ds-currency-input
                v-model="serviceTakerAggregated.grossAmount"
                required />
            </ds-field>
          </ds-col>

          <ds-col size="3">
            <ds-field label="Retenção INSS">
              <ds-currency-input
                v-model="serviceTakerAggregated.inssRetentionAmount"
                required />
            </ds-field>
          </ds-col>
        </ds-row>

        <service-taker-employee-inline-table
          ref="inlineTable"
          :competence="serviceTakerAggregated.competence"
          :allocation-type.sync="serviceTakerAggregated.allocationType"
          :allocations.sync="serviceTakerAggregated.allocations">
        </service-taker-employee-inline-table>

        <ds-footer justify="space-between">
          <ds-button @click="close">Cancelar</ds-button>
          <ds-submit-button>Salvar</ds-submit-button>
        </ds-footer>
      </ds-form>
    </ds-page>
  </ds-rollover>
</template>

<script>
import { dialogService, toasterService } from 'packages/common/ds-components';
import { getBoxInformationErrorMessage } from 'packages/common/utils';
import { SUCCESS_MESSAGE, SERVICE_TAKER_ALLOCATION_TYPES } from '../constants';

import ServiceTakerEmployeeInlineTable from './components/ServiceTakerEmployeeInlineTable';
import { getServiceTaker, getServiceTakersListSearching } from '../../register/serviceTakerService';
import {
  getLastCompetence,
  createServiceTakerAllocations,
  getServiceTakerAllocation,
  getPeriodFormatted,
  updateServiceTakerAllocations,
} from '../serviceTakerAllocationService';

const DEFAULT_SERVICE_TAKER_AGGREGATED = {
  serviceTaker: {
    registrationType: {},
  },
  allocationType: SERVICE_TAKER_ALLOCATION_TYPES.DAY,
  competence: getLastCompetence(),
  allocations: [],
};

const ServiceTakerAllocationRegisterRollover = {
  name: 'ServiceTakerAllocationRollover',
  components: { ServiceTakerEmployeeInlineTable },
  props: {
    allocationId: String,
  },
  data() {
    return {
      serviceTakerAggregated: { ...DEFAULT_SERVICE_TAKER_AGGREGATED },
      serviceTakers: [],
      serviceTakerSelected: null,
      errorMessage: {},
    };
  },
  created() {
    this.initData();
  },
  computed: {
    isEditMode() {
      return !!this.allocationId;
    },
  },
  methods: {
    initData() {
      if (this.isEditMode) {
        this.getServiceTakerAllocation();
      }
    },
    async getServiceTakerAllocation() {
      this.serviceTakerAggregated = await getServiceTakerAllocation(this.allocationId);
    },
    onFetchServiceTaker(serviceTaker) {
      return serviceTaker.id ? getServiceTaker(serviceTaker.id) : Promise.resolve(null);
    },
    onFetchServiceTakerSuccess(serviceTaker) {
      if (!serviceTaker) return;
      this.serviceTakerSelected = {
        value: serviceTaker,
        label: serviceTaker.serviceTakerName,
      };
      this.serviceTakerAggregated.serviceTaker = serviceTaker;
    },
    onQueryServiceTaker(term) {
      const validTerm = term || '';
      return getServiceTakersListSearching(validTerm);
    },
    onQueryServiceTakerSuccess(response) {
      this.serviceTakers = response.content;
    },
    onServiceTakerChange(value) {
      if (!value) {
        this.serviceTakerAggregated.serviceTaker = {
          registration: null,
          registrationType: null,
        };
      }
    },
    onCompetenceChange() {
      const { competence } = this.serviceTakerAggregated;
      this.$refs.inlineTable.reloadRules(competence);
    },
    onSubmit() {
      return this.isEditMode
        ? updateServiceTakerAllocations(this.serviceTakerAggregated)
        : createServiceTakerAllocations(this.serviceTakerAggregated);
    },
    onSubmitSuccess() {
      toasterService.success({
        title: 'Sucesso',
        content: this.isEditMode ? SUCCESS_MESSAGE.EDIT : SUCCESS_MESSAGE.SAVE,
      });
      this.$emit('refresh-list', getPeriodFormatted(this.serviceTakerAggregated.competence));
      this.close();
    },
    onSubmitError(error) {
      if (error.title) {
        toasterService.error(error);
      } else {
        this.errorMessage = getBoxInformationErrorMessage(error);
      }
    },
    close() {
      this.$refs.rollover.close();
    },
    clearRollover() {
      this.serviceTakerAggregated = {
        serviceTaker: {
          registrationType: {},
        },
        competence: getLastCompetence(),
        allocations: [],
      };
    },
  },
};
ServiceTakerAllocationRegisterRollover.open = dialogService
  .buildOpenDialog(ServiceTakerAllocationRegisterRollover);

export default ServiceTakerAllocationRegisterRollover;
</script>
